import React, { useState, useEffect, useCallback } from 'react';
import API_URL from '../config/API_URL';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend
} from 'recharts';

const CUG_API = API_URL;
const POLL_INTERVAL = 60000;

const fonts = `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');`;

const C = {
  bgPrimary: '#0a0e17', bgSecondary: '#111827', bgCard: '#1a1f2e', bgCardHover: '#222839',
  border: '#2a3040', borderGlow: 'rgba(56,189,248,0.15)',
  textPrimary: '#e2e8f0', textSecondary: '#94a3b8', textMuted: '#64748b',
  green: '#22c55e', greenDim: 'rgba(34,197,94,0.15)',
  red: '#ef4444', redDim: 'rgba(239,68,68,0.15)',
  cyan: '#38bdf8', cyanDim: 'rgba(56,189,248,0.12)',
  orange: '#f97316', orangeDim: 'rgba(249,115,22,0.15)',
  purple: '#a78bfa', purpleDim: 'rgba(167,139,250,0.15)',
  yellow: '#facc15', yellowDim: 'rgba(250,204,21,0.12)',
  blue: '#6366f1',
  fontSans: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  fontMono: "'JetBrains Mono', 'Fira Code', monospace",
};

const symbolColors = {
  BTC: { bg: C.orangeDim, color: C.orange },
  ETH: { bg: C.cyanDim, color: C.cyan },
  XRP: { bg: C.purpleDim, color: C.purple },
};

const actionColors = {
  buy_yes: { bg: C.greenDim, color: C.green },
  buy_no: { bg: C.redDim, color: C.red },
  skip: { bg: 'rgba(100,116,139,0.15)', color: C.textMuted },
};

const outcomeColors = {
  win: { bg: C.greenDim, color: C.green },
  loss: { bg: C.redDim, color: C.red },
  pending: { bg: C.yellowDim, color: C.yellow },
};

const S = {
  page: { minHeight: '100vh', background: C.bgPrimary, color: C.textPrimary, fontFamily: C.fontSans, padding: '24px 32px 64px', boxSizing: 'border-box' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  h1: { fontSize: '1.65rem', fontWeight: 700, color: C.textPrimary, margin: 0, letterSpacing: '-0.5px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 12 },
  lastUpdated: { fontSize: '0.75rem', color: C.textMuted, fontFamily: C.fontMono },

  badge: (color, bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', background: bg, color }),
  liveDot: (active) => ({ width: 10, height: 10, borderRadius: '50%', background: active ? C.green : C.red, boxShadow: `0 0 8px ${active ? C.green : C.red}`, animation: active ? 'cug-pulse 2s ease-in-out infinite' : 'none' }),
  stratBadge: { padding: '4px 12px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, fontFamily: C.fontMono, textTransform: 'uppercase', letterSpacing: '0.5px', background: C.cyanDim, color: C.cyan },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 },
  statCard: { background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', transition: 'border-color 0.3s, transform 0.2s' },
  statLabel: { fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: C.textMuted, marginBottom: 6, fontWeight: 500 },
  statValue: (kind) => ({ fontSize: '1.5rem', fontWeight: 700, fontFamily: C.fontMono, color: kind === 'positive' ? C.green : kind === 'negative' ? C.red : C.textPrimary }),

  columns: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 },
  card: { background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${C.border}`, background: C.bgSecondary },
  cardHeaderH2: { fontSize: '0.9rem', fontWeight: 600, margin: 0, color: C.textPrimary, display: 'flex', alignItems: 'center', gap: 8 },
  cardBody: { padding: '16px 20px' },

  chartsColumns: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 },
  chartWrap: { width: '100%', height: 220 },

  sessionGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 },
  sessionItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  sessionLabel: { fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.7px', color: C.textMuted, fontWeight: 500 },
  sessionValue: { fontFamily: C.fontMono, fontSize: '0.95rem', fontWeight: 600, color: C.textPrimary },

  progressTrack: { width: '100%', height: 8, background: C.bgPrimary, borderRadius: 999, overflow: 'hidden', marginTop: 10 },
  progressFill: (pct) => ({ height: '100%', borderRadius: 999, width: `${Math.min(pct, 100)}%`, transition: 'width 0.5s ease', background: `linear-gradient(90deg, ${C.cyan}, ${C.blue})` }),

  symbol: (sym) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, fontFamily: C.fontMono, letterSpacing: '0.5px', background: (symbolColors[sym] || {}).bg || C.cyanDim, color: (symbolColors[sym] || {}).color || C.cyan }),
  action: (act) => ({ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', fontFamily: C.fontMono, background: (actionColors[act] || actionColors.skip).bg, color: (actionColors[act] || actionColors.skip).color }),
  outcome: (o) => ({ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', fontFamily: C.fontMono, background: (outcomeColors[o] || outcomeColors.pending).bg, color: (outcomeColors[o] || outcomeColors.pending).color }),

  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' },
  th: { textAlign: 'left', padding: '10px 14px', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.7px', color: C.textMuted, fontWeight: 600, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', background: C.bgSecondary },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(42,48,64,0.5)', fontFamily: C.fontMono, fontSize: '0.82rem', whiteSpace: 'nowrap', color: C.textPrimary, background: C.bgCard },
  tdReason: { padding: '10px 14px', borderBottom: '1px solid rgba(42,48,64,0.5)', whiteSpace: 'normal', maxWidth: 380, fontFamily: C.fontSans, fontSize: '0.78rem', color: C.textSecondary, lineHeight: 1.4, background: C.bgCard },

  decisionsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 14 },
  decisionCard: { background: C.bgSecondary, border: `1px solid ${C.border}`, borderRadius: 8, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
  decisionTopRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  decisionReason: { fontSize: '0.78rem', color: C.textSecondary, lineHeight: 1.45 },

  winSummary: { display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 },
  winStat: { display: 'flex', flexDirection: 'column', gap: 2 },
  winLabel: { fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.7px', color: C.textMuted, fontWeight: 500 },
  winValue: (color) => ({ fontFamily: C.fontMono, fontSize: '1.15rem', fontWeight: 700, color }),

  costEntries: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  costEntry: { background: C.bgSecondary, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 18px', textAlign: 'center' },
  costDate: { fontSize: '0.72rem', color: C.textMuted, marginBottom: 4 },
  costAmount: { fontFamily: C.fontMono, fontSize: '1.2rem', fontWeight: 700, color: C.yellow },

  confWrap: { display: 'flex', alignItems: 'center', gap: 6 },
  confBar: { width: 50, height: 5, background: C.bgPrimary, borderRadius: 999, overflow: 'hidden' },
  confFill: (val) => ({ height: '100%', borderRadius: 999, width: `${(val * 100).toFixed(0)}%`, background: val >= 0.7 ? C.green : val >= 0.4 ? C.yellow : C.red, transition: 'width 0.3s' }),

  scrollArea: { maxHeight: 500, overflowY: 'auto', background: C.bgCard },
  loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 20 },
  spinner: { width: 48, height: 48, border: `3px solid ${C.border}`, borderTopColor: C.cyan, borderRadius: '50%', animation: 'cug-spin 0.8s linear infinite' },
  errorWrap: { textAlign: 'center', padding: 48, color: C.red },
  retryBtn: { marginTop: 16, padding: '8px 24px', background: C.redDim, color: C.red, border: `1px solid ${C.red}`, borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
  noData: { color: C.textMuted, textAlign: 'center', padding: '32px 0' },
};

const KEYFRAMES_ID = 'cug-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(KEYFRAMES_ID)) {
  const style = document.createElement('style');
  style.id = KEYFRAMES_ID;
  style.textContent = `
    ${fonts}
    @keyframes cug-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.3)} }
    @keyframes cug-spin { to{transform:rotate(360deg)} }
    @keyframes cug-fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
    .cug-scroll::-webkit-scrollbar { width:6px }
    .cug-scroll::-webkit-scrollbar-track { background:transparent }
    .cug-scroll::-webkit-scrollbar-thumb { background:${C.border}; border-radius:999px }
    .cug-tr-hover:hover td { background:${C.bgCardHover} !important }
    .cug-stat-hover:hover { border-color:${C.borderGlow} !important; transform:translateY(-2px) }
  `;
  document.head.appendChild(style);
}

/* ══════════════════════════════════════
   Helpers
   ══════════════════════════════════════ */

function formatTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }); } catch { return iso; }
}
function formatDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return iso; }
}
function formatTimeShort(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return ''; }
}
function fmtUsd(v) { if (v == null) return '—'; const n = Number(v); return `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`; }
function centsToDollars(c) { return c == null ? '—' : fmtUsd(c / 100); }
function pnlKind(v) { if (v == null) return 'neutral'; return Number(v) >= 0 ? 'positive' : 'negative'; }
function pnlColor(v) { if (v == null) return C.textMuted; return Number(v) >= 0 ? C.green : C.red; }
function guessSym(ticker) { if (ticker.includes('BTC')) return 'BTC'; if (ticker.includes('ETH')) return 'ETH'; if (ticker.includes('XRP')) return 'XRP'; return ''; }

/* ══════════════════════════════════════
   Chart data builders
   ══════════════════════════════════════ */

/**
 * Builds cumulative PnL / balance chart data from settled wins.
 * Each point = after that trade settles.
 * If session.balance_start_usd is available, we offset by that.
 * If sessionStartedAt is provided, only include trades from the current session.
 */
function buildBalanceChartData(wins, balanceStart, sessionStartedAt) {
  let filtered = [...wins].filter(w => w.outcome !== 'pending' && w.settled_at);

  // Fix: only include trades from the current session to avoid inflated cumulative balance
  if (sessionStartedAt) {
    const sessionStart = new Date(sessionStartedAt);
    filtered = filtered.filter(w => new Date(w.settled_at) >= sessionStart);
  }

  const settled = filtered.sort((a, b) => new Date(a.settled_at) - new Date(b.settled_at));

  if (settled.length === 0) return [];

  let cumulative = balanceStart != null ? Number(balanceStart) * 100 : 0; // work in cents
  const startLabel = formatTimeShort(settled[0].settled_at);

  const points = [{ label: startLabel, balance: cumulative / 100, tradeNum: 0 }];

  settled.forEach((w, i) => {
    cumulative += (w.pnl_cents || 0);
    points.push({
      label: formatTimeShort(w.settled_at),
      balance: parseFloat((cumulative / 100).toFixed(2)),
      tradeNum: i + 1,
      outcome: w.outcome,
    });
  });

  return points;
}

/**
 * Builds rolling win-rate chart data from settled wins.
 * Each point = win rate after that trade settles.
 */
function buildWinRateChartData(wins) {
  const settled = [...wins]
    .filter(w => w.outcome === 'win' || w.outcome === 'loss')
    .sort((a, b) => new Date(a.settled_at) - new Date(b.settled_at));

  if (settled.length === 0) return [];

  let wins_ = 0;
  let total = 0;

  return settled.map((w, i) => {
    if (w.outcome === 'win') wins_++;
    total++;
    return {
      label: formatTimeShort(w.settled_at),
      winRate: parseFloat(((wins_ / total) * 100).toFixed(1)),
      tradeNum: i + 1,
    };
  });
}

/* ══════════════════════════════════════
   Custom Tooltip components
   ══════════════════════════════════════ */

const tooltipStyle = {
  background: C.bgSecondary,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: '8px 14px',
  fontFamily: C.fontMono,
  fontSize: '0.78rem',
  color: C.textPrimary,
};

function BalanceTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  return (
    <div style={tooltipStyle}>
      <div style={{ color: C.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ color: val >= 0 ? C.green : C.red }}>Balance: {fmtUsd(val)}</div>
    </div>
  );
}

function WinRateTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  return (
    <div style={tooltipStyle}>
      <div style={{ color: C.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ color: val >= 50 ? C.green : C.red }}>Win Rate: {val}%</div>
    </div>
  );
}

/* ══════════════════════════════════════
   Charts section component
   ══════════════════════════════════════ */

function PerformanceCharts({ wins, balanceStart, sessionStartedAt }) {
  const balanceData = buildBalanceChartData(wins || [], balanceStart, sessionStartedAt);
  const winRateData = buildWinRateChartData(wins || []);

  const hasData = balanceData.length > 1 || winRateData.length > 1;

  if (!hasData) {
    return (
      <div style={S.card}>
        <div style={S.cardHeader}>
          <h2 style={S.cardHeaderH2}>📈 Performance Charts</h2>
        </div>
        <div style={S.cardBody}>
          <p style={S.noData}>No settled trades yet — charts will appear once trades resolve.</p>
        </div>
      </div>
    );
  }

  // Determine dynamic Y domain for balance chart with padding
  const balanceValues = balanceData.map(d => d.balance).filter(v => v != null);
  const minBal = Math.min(...balanceValues);
  const maxBal = Math.max(...balanceValues);
  const balPad = Math.max((maxBal - minBal) * 0.15, 1);
  const balDomain = [parseFloat((minBal - balPad).toFixed(2)), parseFloat((maxBal + balPad).toFixed(2))];

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <h2 style={S.cardHeaderH2}>📈 Performance Charts</h2>
        <span style={S.lastUpdated}>{(wins || []).filter(w => w.outcome !== 'pending').length} settled trades</span>
      </div>
      <div style={{ ...S.cardBody, paddingBottom: 24 }}>
        <div style={S.chartsColumns}>

          {/* ── Balance Chart ── */}
          <div>
            <div style={{ fontSize: '0.75rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 12, fontWeight: 600 }}>
              Cumulative Balance
            </div>
            {balanceData.length > 1 ? (
              <div style={S.chartWrap}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={balanceData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <defs>
                      <linearGradient id="balGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={C.cyan} />
                        <stop offset="100%" stopColor={C.blue} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: C.textMuted, fontSize: 10, fontFamily: C.fontMono }}
                      tickLine={false}
                      axisLine={{ stroke: C.border }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      domain={balDomain}
                      tick={{ fill: C.textMuted, fontSize: 10, fontFamily: C.fontMono }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={v => `$${v}`}
                      width={60}
                    />
                    <Tooltip content={<BalanceTooltip />} />
                    {balanceStart != null && (
                      <ReferenceLine
                        y={Number(balanceStart)}
                        stroke={C.textMuted}
                        strokeDasharray="4 4"
                        label={{ value: 'Start', fill: C.textMuted, fontSize: 10, position: 'right' }}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="balance"
                      stroke="url(#balGrad)"
                      strokeWidth={2.5}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        if (!payload.outcome) return <circle key={`dot-${cx}`} cx={cx} cy={cy} r={3} fill={C.cyan} stroke="none" />;
                        const color = payload.outcome === 'win' ? C.green : C.red;
                        return <circle key={`dot-${cx}`} cx={cx} cy={cy} r={4} fill={color} stroke={C.bgCard} strokeWidth={1.5} />;
                      }}
                      activeDot={{ r: 5, fill: C.cyan, stroke: C.bgCard, strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p style={S.noData}>Need at least 2 settled trades</p>
            )}
          </div>

          {/* ── Win Rate Chart ── */}
          <div>
            <div style={{ fontSize: '0.75rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 12, fontWeight: 600 }}>
              Rolling Win Rate
            </div>
            {winRateData.length > 1 ? (
              <div style={S.chartWrap}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={winRateData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: C.textMuted, fontSize: 10, fontFamily: C.fontMono }}
                      tickLine={false}
                      axisLine={{ stroke: C.border }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fill: C.textMuted, fontSize: 10, fontFamily: C.fontMono }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={v => `${v}%`}
                      width={44}
                    />
                    <Tooltip content={<WinRateTooltip />} />
                    <ReferenceLine
                      y={50}
                      stroke={C.textMuted}
                      strokeDasharray="4 4"
                      label={{ value: '50%', fill: C.textMuted, fontSize: 10, position: 'right' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="winRate"
                      strokeWidth={2.5}
                      stroke={C.green}
                      activeDot={{ r: 5, stroke: C.bgCard, strokeWidth: 2 }}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        const color = payload.winRate >= 50 ? C.green : C.red;
                        return <circle key={`wr-dot-${cx}`} cx={cx} cy={cy} r={3.5} fill={color} stroke={C.bgCard} strokeWidth={1.5} />;
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>



              </div>
            ) : (
              <p style={S.noData}>Need at least 2 settled (non-pending) trades</p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   Main component
   ══════════════════════════════════════ */

function CugPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${CUG_API}/api/cug/dashboard`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err) {
      console.error('CUG fetch error:', err);
      if (!data) setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchDashboard(); const id = setInterval(fetchDashboard, POLL_INTERVAL); return () => clearInterval(id); }, [fetchDashboard]);

  if (loading && !data) return (
    <div style={S.page}>
      <div style={S.loadingWrap}>
        <div style={S.spinner} />
        <span style={{ color: C.textMuted }}>Connecting to trading bot…</span>
      </div>
    </div>
  );

  if (error && !data) return (
    <div style={S.page}>
      <div style={S.errorWrap}>
        <h2>⚠ Connection Failed</h2>
        <p>{error}</p>
        <button style={S.retryBtn} onClick={() => { setLoading(true); fetchDashboard(); }}>Retry</button>
      </div>
    </div>
  );

  const { status, session, costs, trades, decisions, wins, arena_state } = data || {};
  const isActive = session?.active ?? false;
  const mode = status?.mode ?? 'unknown';
  const strategyMode = status?.strategy_mode ?? '—';
  const balance = status?.balance_usd;
  const pnl = status?.session?.pnl_usd;
  const elapsedMin = status?.session?.elapsed_minutes ?? 0;
  const remainingMin = status?.session?.remaining_minutes ?? 0;
  const totalMin = elapsedMin + remainingMin;
  const progressPct = totalMin > 0 ? (elapsedMin / totalMin) * 100 : 0;

  const totalWins = (wins || []).filter(w => w.outcome === 'win').length;
  const totalLosses = (wins || []).filter(w => w.outcome === 'loss').length;
  const pendingCount = (wins || []).filter(w => w.outcome === 'pending').length;
  const winRate = (totalWins + totalLosses) > 0 ? ((totalWins / (totalWins + totalLosses)) * 100).toFixed(1) : '—';
  const totalPnlCents = (wins || []).reduce((s, w) => s + (w.pnl_cents || 0), 0);

  return (
    <div style={{ ...S.page, animation: 'cug-fadeIn 0.35s ease forwards' }}>

      {/* ════ HEADER ════ */}
      <header style={S.header}>
        <div style={S.headerLeft}>
          <h1 style={S.h1}>📊 Kalshi Trading Dashboard</h1>
          <span style={S.badge(isActive ? C.green : C.red, isActive ? C.greenDim : C.redDim)}>
            <span style={S.liveDot(isActive)} />
            {isActive ? 'LIVE' : 'INACTIVE'}
          </span>
          {mode !== 'unknown' && <span style={S.badge(C.green, C.greenDim)}>{mode}</span>}
          {strategyMode === 'arena'
            ? <span style={{ ...S.stratBadge, background: C.purpleDim, color: C.purple }}>⚔️ ARENA</span>
            : <span style={S.stratBadge}>{strategyMode}</span>
          }
        </div>
        <div style={S.headerRight}>
          <span style={S.lastUpdated}>Last cycle: {formatTime(status?.last_cycle)}</span>
        </div>
      </header>

      {/* ════ STAT CARDS ════ */}
      <div style={S.statsRow}>
        <StatCard label="Balance" value={fmtUsd(balance)} kind={pnlKind(balance)} />
        <StatCard label="Session PnL" value={fmtUsd(pnl)} kind={pnlKind(pnl)} />
        <StatCard label="Win Rate" value={winRate !== '—' ? `${winRate}%` : '—'} kind={Number(winRate) >= 50 ? 'positive' : Number(winRate) > 0 ? 'negative' : 'neutral'} />
        <StatCard label="Total PnL (Settled)" value={centsToDollars(totalPnlCents)} kind={pnlKind(totalPnlCents)} />
        <StatCard label="Open Positions" value={String(status?.open_positions?.length ?? 0)} kind="neutral" />
        <StatCard label="Pending Orders" value={String(status?.pending_orders_count ?? 0)} kind="neutral" />
      </div>

      {/* ════ PERFORMANCE CHARTS ════ */}
      <PerformanceCharts wins={wins} balanceStart={session?.balance_start_usd} sessionStartedAt={session?.started_at} />

      {/* ════ ARENA SECTION ════ */}
      {strategyMode === 'arena' && (status?.arena || arena_state) && (
        <ArenaSection arena={status?.arena} arenaState={arena_state} />
      )}

      {/* ════ SESSION + POSITIONS ════ */}
      <div style={S.columns}>
        <div style={S.card}>
          <div style={S.cardHeader}><h2 style={S.cardHeaderH2}>⏱ Session</h2></div>
          <div style={S.cardBody}>
            <div style={S.sessionGrid}>
              <SI label="Started" value={formatDateTime(session?.started_at)} />
              <SI label="Duration" value={`${session?.duration_minutes ?? '—'} min`} />
              <SI label="Elapsed" value={`${elapsedMin.toFixed(1)} min`} />
              <SI label="Remaining" value={`${remainingMin.toFixed(1)} min`} />
              <SI label="Start Balance" value={fmtUsd(session?.balance_start_usd)} />
              <SI label="Profit Target" value={fmtUsd(status?.session?.profit_target)} />
              <SI label="Max Loss" value={fmtUsd(status?.session?.max_loss)} />
              <SI label="Monitor Only" value={session?.monitoring_only ? 'Yes' : 'No'} />
            </div>
            <div style={S.progressTrack}><div style={S.progressFill(progressPct)} /></div>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardHeader}><h2 style={S.cardHeaderH2}>📌 Open Positions</h2></div>
          <div style={S.cardBody}>
            {(!status?.open_positions || status.open_positions.length === 0)
              ? <p style={S.noData}>No open positions</p>
              : <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead><tr><th style={S.th}>Ticker</th><th style={S.th}>Net Position</th></tr></thead>
                  <tbody>
                    {status.open_positions.map((p, i) => {
                      const sym = guessSym(p.ticker);
                      return (
                        <tr key={i} className="cug-tr-hover">
                          <td style={S.td}>{sym && <span style={S.symbol(sym)}>{sym}</span>} {p.ticker}</td>
                          <td style={{ ...S.td, color: pnlColor(p.position_net) }}>{p.position_net}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            }
          </div>
        </div>
      </div>

      {/* ════ CURRENT DECISIONS ════ */}
      {status?.decisions?.length > 0 && (
        <div style={S.card}>
          <div style={S.cardHeader}><h2 style={S.cardHeaderH2}>🧠 Current Cycle Decisions</h2></div>
          <div style={S.cardBody}>
            <div style={S.decisionsGrid}>
              {status.decisions.map((d, i) => (
                <div style={S.decisionCard} key={i}>
                  <div style={S.decisionTopRow}>
                    <span style={S.symbol(d.symbol)}>{d.symbol}</span>
                    <span style={S.action(d.action)}>{d.action}</span>
                  </div>
                  <span style={S.decisionReason}>{d.reason || 'No additional reason'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ════ WIN TRACKER ════ */}
      {wins?.length > 0 && (
        <div style={S.card}>
          <div style={S.cardHeader}>
            <h2 style={S.cardHeaderH2}>🏆 Win Tracker</h2>
            <span style={S.lastUpdated}>{wins.length} settled trades</span>
          </div>
          <div style={S.cardBody}>
            <div style={S.winSummary}>
              <WinStat label="Wins" value={totalWins} color={C.green} />
              <WinStat label="Losses" value={totalLosses} color={C.red} />
              <WinStat label="Pending" value={pendingCount} color={C.yellow} />
              <WinStat label="Win Rate" value={`${winRate}%`} color={Number(winRate) >= 50 ? C.green : C.red} />
              <WinStat label="Total PnL" value={centsToDollars(totalPnlCents)} color={pnlColor(totalPnlCents)} />
            </div>
            <div style={S.scrollArea} className="cug-scroll">
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Symbol</th><th style={S.th}>Action</th><th style={S.th}>Price</th>
                  <th style={S.th}>EV</th><th style={S.th}>Confidence</th><th style={S.th}>Outcome</th>
                  <th style={S.th}>PnL</th><th style={S.th}>Settled</th>
                </tr></thead>
                <tbody>
                  {[...wins].reverse().map((w, i) => (
                    <tr key={i} className="cug-tr-hover">
                      <td style={S.td}><span style={S.symbol(w.symbol)}>{w.symbol}</span></td>
                      <td style={S.td}><span style={S.action(w.action)}>{w.action}</span></td>
                      <td style={S.td}>{w.price_cents}¢</td>
                      <td style={S.td}>{(w.ev_estimate * 100).toFixed(1)}%</td>
                      <td style={S.td}><ConfMeter v={w.algo_confidence} /></td>
                      <td style={S.td}><span style={S.outcome(w.outcome)}>{w.outcome}</span></td>
                      <td style={{ ...S.td, color: pnlColor(w.pnl_cents) }}>{w.pnl_cents != null ? `${w.pnl_cents}¢` : '—'}</td>
                      <td style={S.td}>{formatTime(w.settled_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ════ RECENT TRADES ════ */}
      {trades?.length > 0 && (
        <div style={S.card}>
          <div style={S.cardHeader}>
            <h2 style={S.cardHeaderH2}>💹 Recent Trades</h2>
            <span style={S.lastUpdated}>{trades.length} trades</span>
          </div>
          <div style={S.cardBody}>
            <div style={S.scrollArea} className="cug-scroll">
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Time</th><th style={S.th}>Symbol</th><th style={S.th}>Action</th>
                  <th style={S.th}>Contracts</th><th style={S.th}>Price</th><th style={S.th}>EV</th>
                  <th style={S.th}>Confidence</th><th style={S.th}>Status</th><th style={S.th}>Reasoning</th>
                </tr></thead>
                <tbody>
                  {trades.map((t, i) => (
                    <tr key={i} className="cug-tr-hover">
                      <td style={S.td}>{formatTime(t.timestamp)}</td>
                      <td style={S.td}><span style={S.symbol(t.symbol)}>{t.symbol}</span></td>
                      <td style={S.td}><span style={S.action(t.action)}>{t.action}</span></td>
                      <td style={S.td}>{t.contracts}</td>
                      <td style={S.td}>{t.price_cents}¢</td>
                      <td style={S.td}>{(t.ev_estimate * 100).toFixed(1)}%</td>
                      <td style={S.td}><ConfMeter v={t.ai_confidence} /></td>
                      <td style={S.td}><span style={S.action(t.order_status === 'executed' ? 'buy_yes' : 'skip')}>{t.order_status || '—'}</span></td>
                      <td style={S.tdReason}>{t.reasoning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ════ DECISION LOG ════ */}
      {decisions?.length > 0 && (
        <div style={S.card}>
          <div style={S.cardHeader}>
            <h2 style={S.cardHeaderH2}>📋 Decision Log</h2>
            <span style={S.lastUpdated}>{decisions.length} decisions</span>
          </div>
          <div style={S.cardBody}>
            <div style={S.scrollArea} className="cug-scroll">
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Time</th><th style={S.th}>Symbol</th><th style={S.th}>Action</th>
                  <th style={S.th}>Direction</th><th style={S.th}>Confidence</th><th style={S.th}>EV</th>
                  <th style={S.th}>Reasoning</th>
                </tr></thead>
                <tbody>
                  {decisions.map((d, i) => (
                    <tr key={i} className="cug-tr-hover">
                      <td style={S.td}>{formatTime(d.timestamp)}</td>
                      <td style={S.td}><span style={S.symbol(d.symbol)}>{d.symbol}</span></td>
                      <td style={S.td}><span style={S.action(d.action)}>{d.action}</span></td>
                      <td style={S.td}>{d.algo_direction}</td>
                      <td style={S.td}><ConfMeter v={d.algo_confidence} /></td>
                      <td style={S.td}>{d.ev_estimate > 0 ? `${(d.ev_estimate * 100).toFixed(1)}%` : '—'}</td>
                      <td style={S.tdReason}>{d.reasoning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ════ API COSTS ════ */}
      {costs && Object.keys(costs).length > 0 && (
        <div style={S.card}>
          <div style={S.cardHeader}><h2 style={S.cardHeaderH2}>💰 API Costs</h2></div>
          <div style={S.cardBody}>
            <div style={S.costEntries}>
              {Object.entries(costs).map(([date, amount]) => (
                <div style={S.costEntry} key={date}>
                  <div style={S.costDate}>{date}</div>
                  <div style={S.costAmount}>${Number(amount).toFixed(4)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   Arena Section Component — Expanded
   ══════════════════════════════════════ */

const arenaStyles = {
  wrapper: { marginBottom: 20 },
  metaRow: {
    display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap',
  },
  metaItem: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: '0.78rem', color: C.textSecondary, fontFamily: C.fontMono,
  },
  agentGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20,
  },
  agentCard: (isLeader) => ({
    background: C.bgCard,
    border: `1px solid ${isLeader ? 'rgba(34,197,94,0.4)' : C.border}`,
    borderRadius: 12,
    overflow: 'hidden',
    transition: 'border-color 0.3s',
    ...(isLeader ? { boxShadow: '0 0 20px rgba(34,197,94,0.08)' } : {}),
  }),
  agentHeader: (color) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: `1px solid ${C.border}`,
    background: `linear-gradient(135deg, ${C.bgSecondary}, ${color}15)`,
  }),
  agentName: {
    fontSize: '1.1rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10,
  },
  modelBadge: {
    fontSize: '0.68rem', fontWeight: 600, fontFamily: C.fontMono,
    padding: '3px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.06)',
    color: C.textSecondary, letterSpacing: '0.3px',
  },
  aliveDot: (alive) => ({
    width: 8, height: 8, borderRadius: '50%',
    background: alive ? C.green : C.red,
    boxShadow: `0 0 6px ${alive ? C.green : C.red}`,
  }),
  agentBody: { padding: '16px 20px' },
  agentStatsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 16,
  },
  agentStat: {
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  agentStatLabel: {
    fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.7px',
    color: C.textMuted, fontWeight: 500,
  },
  agentStatValue: (color) => ({
    fontFamily: C.fontMono, fontSize: '1.1rem', fontWeight: 700, color: color || C.textPrimary,
  }),
  budgetSection: {
    borderTop: `1px solid ${C.border}`, paddingTop: 14,
  },
  budgetRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '0.78rem', color: C.textSecondary, marginBottom: 6,
  },
  budgetBarTrack: {
    width: '100%', height: 6, background: C.bgPrimary, borderRadius: 999,
    overflow: 'hidden', marginTop: 4, marginBottom: 10,
  },
  budgetBarFill: (pct, color) => ({
    height: '100%', borderRadius: 999, width: `${Math.min(pct, 100)}%`,
    background: color, transition: 'width 0.5s ease',
  }),
  vsLabel: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.8rem', fontWeight: 800, color: C.textMuted,
    letterSpacing: '2px', opacity: 0.3, alignSelf: 'center',
  },
  leaderBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 999,
    fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
    background: C.greenDim, color: C.green, letterSpacing: '0.5px',
  },
  recentTradesWrap: { marginTop: 12 },
  recentTradesLabel: {
    fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.7px',
    color: C.textMuted, fontWeight: 600, marginBottom: 8,
  },
  // ── Scoreboard styles ──
  scoreboard: {
    display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 0,
    background: `linear-gradient(135deg, rgba(56,189,248,0.06), rgba(167,139,250,0.06))`,
    border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden',
    marginBottom: 20,
  },
  scoreAgent: (align) => ({
    padding: '24px 28px', display: 'flex', flexDirection: 'column',
    alignItems: align === 'left' ? 'flex-start' : 'flex-end', gap: 6,
  }),
  scoreAgentName: (color) => ({
    fontSize: '1rem', fontWeight: 700, color, display: 'flex', alignItems: 'center', gap: 8,
  }),
  scorePnl: (color) => ({
    fontSize: '2rem', fontWeight: 800, fontFamily: C.fontMono, color,
    letterSpacing: '-1px', lineHeight: 1.1,
  }),
  scoreRecord: {
    fontSize: '0.85rem', fontFamily: C.fontMono, display: 'flex', gap: 8,
    color: C.textSecondary, marginTop: 2,
  },
  scoreRecordWin: { color: C.green, fontWeight: 600 },
  scoreRecordLoss: { color: C.red, fontWeight: 600 },
  scoreCenter: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '24px 20px', borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
    minWidth: 100,
  },
  scoreVs: {
    fontSize: '1.4rem', fontWeight: 800, color: C.textMuted, opacity: 0.4,
    letterSpacing: '3px',
  },
  scoreMeta: {
    fontSize: '0.68rem', color: C.textMuted, fontFamily: C.fontMono,
    textAlign: 'center', marginTop: 8, lineHeight: 1.6,
  },
  scoreLeadBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 8px', borderRadius: 999, fontSize: '0.65rem', fontWeight: 700,
    textTransform: 'uppercase', background: C.greenDim, color: C.green, marginTop: 4,
  },
  // ── H2H Comparison bars ──
  h2hWrapper: { marginBottom: 20 },
  h2hRow: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
  },
  h2hLabel: {
    width: 90, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.6px',
    color: C.textMuted, fontWeight: 600, textAlign: 'right', flexShrink: 0,
  },
  h2hBarWrap: {
    flex: 1, display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden',
    background: C.bgPrimary, position: 'relative',
  },
  h2hBarSide: (pct, color, align) => ({
    width: `${pct}%`, background: color, display: 'flex', alignItems: align,
    justifyContent: align === 'flex-start' ? 'flex-start' : 'flex-end',
    padding: '0 8px', transition: 'width 0.5s ease', minWidth: pct > 0 ? 32 : 0,
  }),
  h2hBarText: {
    fontSize: '0.7rem', fontWeight: 700, fontFamily: C.fontMono, color: '#fff',
    lineHeight: '24px', whiteSpace: 'nowrap',
  },
  h2hDivider: {
    width: 2, height: 24, background: C.bgCard, flexShrink: 0,
  },
  // ── Symbol breakdown ──
  symbolGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 10,
  },
  symbolCard: (sym) => ({
    background: (symbolColors[sym] || {}).bg || C.cyanDim,
    border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px',
    display: 'flex', flexDirection: 'column', gap: 4,
  }),
  symbolCardName: (sym) => ({
    fontSize: '0.78rem', fontWeight: 700, fontFamily: C.fontMono,
    color: (symbolColors[sym] || {}).color || C.cyan,
  }),
  symbolCardStat: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '0.7rem', color: C.textSecondary,
  },
  symbolCardVal: (color) => ({
    fontFamily: C.fontMono, fontWeight: 600, color: color || C.textPrimary,
  }),
  // ── Decision quality ──
  qualityGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 12,
    borderTop: `1px solid ${C.border}`, paddingTop: 14,
  },
  qualityItem: {
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  qualityLabel: {
    fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.6px',
    color: C.textMuted, fontWeight: 500,
  },
  qualityValue: (color) => ({
    fontFamily: C.fontMono, fontSize: '0.95rem', fontWeight: 700, color: color || C.textPrimary,
  }),
  // ── Activity feed tabs ──
  tabRow: {
    display: 'flex', gap: 0, borderBottom: `1px solid ${C.border}`, marginBottom: 0,
  },
  tab: (active) => ({
    padding: '10px 20px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
    color: active ? C.cyan : C.textMuted,
    borderBottom: active ? `2px solid ${C.cyan}` : '2px solid transparent',
    background: 'transparent', border: 'none', fontFamily: C.fontSans,
    transition: 'color 0.2s',
  }),
};

/* ── Arena helper: compute symbol breakdown for an agent ── */
function computeSymbolBreakdown(trades) {
  const syms = {};
  (trades || []).forEach(t => {
    if (t.action === 'skip') return;
    const sym = t.symbol || '?';
    if (!syms[sym]) syms[sym] = { trades: 0, wins: 0, losses: 0, pnl: 0 };
    syms[sym].trades++;
    if (t.outcome === 'win') syms[sym].wins++;
    if (t.outcome === 'loss') syms[sym].losses++;
    syms[sym].pnl += (t.pnl_cents || 0);
  });
  return syms;
}

/* ── Arena helper: compute decision quality stats ── */
function computeDecisionQuality(trades) {
  const all = trades || [];
  const placed = all.filter(t => t.action !== 'skip');
  const skipped = all.filter(t => t.action === 'skip');
  const parseFails = all.filter(t => t.reasoning === 'Parse failed').length;
  const tradeSkipRatio = skipped.length > 0 ? (placed.length / skipped.length).toFixed(2) : placed.length > 0 ? '∞' : '0';

  // Try to extract confidence from reasoning text
  const confidences = placed.map(t => {
    const m = (t.reasoning || '').match(/(\d+(?:\.\d+)?)%\s*confidence/i);
    return m ? parseFloat(m[1]) : null;
  }).filter(v => v != null);
  const avgConf = confidences.length > 0 ? (confidences.reduce((a, b) => a + b, 0) / confidences.length).toFixed(1) : null;

  // Selectivity: what % of evaluations resulted in a trade
  const selectivity = all.length > 0 ? ((placed.length / all.length) * 100).toFixed(1) : '0.0';

  return { tradeSkipRatio, avgConf, parseFails, selectivity, totalEvals: all.length, totalPlaced: placed.length };
}

/* ── Arena helper: build per-agent PnL chart data from trades array ── */
function buildAgentPnlChartData(trades, startingBudget) {
  const placed = (trades || []).filter(t => t.action !== 'skip' && t.outcome !== 'pending');
  const sorted = [...placed].sort((a, b) => new Date(a.settled_at || a.placed_at) - new Date(b.settled_at || b.placed_at));
  if (sorted.length === 0) return [];

  let cumPnl = 0;
  const baseline = startingBudget != null ? Number(startingBudget) : 0;
  const points = [{ label: '#0', balance: baseline, tradeNum: 0 }];
  sorted.forEach((t, i) => {
    cumPnl += (t.pnl_cents || 0);
    points.push({
      label: `#${i + 1}`,
      balance: parseFloat((baseline + cumPnl / 100).toFixed(2)),
      tradeNum: i + 1,
      outcome: t.outcome,
    });
  });
  return points;
}

function ArenaSection({ arena, arenaState }) {
  const [activeTab, setActiveTab] = useState('alpha');

  // Merge data from status.arena and arena_state.json
  const alpha = arena?.alpha || arenaState?.alpha || {};
  const beta = arena?.beta || arenaState?.beta || {};
  const cycles = arena?.cycles_completed ?? arenaState?.cycles_completed ?? 0;
  const arenaStarted = arena?.arena_started_at ?? arenaState?.arena_started_at;

  const alphaPnl = alpha.pnl_usd ?? (alpha.stats?.total_pnl_cents ?? 0) / 100;
  const betaPnl = beta.pnl_usd ?? (beta.stats?.total_pnl_cents ?? 0) / 100;
  const alphaWins = alpha.stats?.wins ?? 0;
  const alphaLosses = alpha.stats?.losses ?? 0;
  const betaWins = beta.stats?.wins ?? 0;
  const betaLosses = beta.stats?.losses ?? 0;
  const alphaTotalTrades = alpha.stats?.total_trades ?? 0;
  const betaTotalTrades = beta.stats?.total_trades ?? 0;
  const alphaSkips = alpha.stats?.skips ?? 0;
  const betaSkips = beta.stats?.skips ?? 0;
  const alphaWR = (alphaWins + alphaLosses) > 0 ? ((alphaWins / (alphaWins + alphaLosses)) * 100).toFixed(1) : '0.0';
  const betaWR = (betaWins + betaLosses) > 0 ? ((betaWins / (betaWins + betaLosses)) * 100).toFixed(1) : '0.0';
  const alphaLeading = alphaPnl >= betaPnl;
  const pnlDiff = Math.abs(alphaPnl - betaPnl);

  const alphaColor = C.cyan;
  const betaColor = C.purple;

  // Decision quality
  const alphaQuality = computeDecisionQuality(alpha.trades);
  const betaQuality = computeDecisionQuality(beta.trades);

  // Symbol breakdowns
  const alphaSymbols = computeSymbolBreakdown(alpha.trades);
  const betaSymbols = computeSymbolBreakdown(beta.trades);

  // PnL chart data
  const alphaPnlChart = buildAgentPnlChartData(alpha.trades, alpha.starting_trading_budget);
  const betaPnlChart = buildAgentPnlChartData(beta.trades, beta.starting_trading_budget);

  function getModelShort(model) {
    if (!model) return '—';
    if (model.includes('sonnet')) return 'Sonnet 4.5';
    if (model.includes('haiku')) return 'Haiku 4.5';
    if (model.includes('opus')) return 'Opus';
    return model.split('-').slice(0, 2).join(' ');
  }

  function tokenPct(agent) {
    const budget = agent.starting_token_budget ?? agent.token_budget_usd ?? 1;
    const spent = agent.token_spent_usd ?? 0;
    return Math.max(0, ((budget - spent) / budget) * 100);
  }

  function tradingPct(agent) {
    const budget = agent.starting_trading_budget ?? agent.trading_budget_usd ?? 1;
    const pnl = (agent.stats?.total_pnl_cents ?? 0) / 100;
    return Math.max(0, Math.min(100, ((budget + pnl) / budget) * 100));
  }

  /* ── H2H comparison bar helper ── */
  function H2HBar({ label, alphaVal, betaVal, formatFn, invert }) {
    const total = (alphaVal || 0) + (betaVal || 0);
    let aPct = total > 0 ? ((alphaVal || 0) / total) * 100 : 50;
    let bPct = total > 0 ? ((betaVal || 0) / total) * 100 : 50;
    if (total === 0) { aPct = 50; bPct = 50; }
    const fmt = formatFn || (v => String(v));
    return (
      <div style={arenaStyles.h2hRow}>
        <div style={arenaStyles.h2hLabel}>{label}</div>
        <div style={arenaStyles.h2hBarWrap}>
          <div style={arenaStyles.h2hBarSide(aPct, 'rgba(56,189,248,0.5)', 'flex-start')}>
            <span style={arenaStyles.h2hBarText}>{fmt(alphaVal)}</span>
          </div>
          <div style={arenaStyles.h2hDivider} />
          <div style={arenaStyles.h2hBarSide(bPct, 'rgba(167,139,250,0.5)', 'flex-end')}>
            <span style={arenaStyles.h2hBarText}>{fmt(betaVal)}</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── Per-agent card with expanded info ── */
  function renderAgentCard(agent, agentPnl, agentWR, agentWins, agentLosses, color, isLeader, chartData, quality, symbolBreakdown) {
    const name = agent.name || agent.agent_id || 'Agent';
    const model = agent.model || '—';
    const alive = agent.alive !== false;
    const totalTrades = agent.stats?.total_trades ?? 0;
    const skips = agent.stats?.skips ?? 0;
    const tokenBudget = agent.token_budget_usd ?? agent.starting_token_budget ?? 0;
    const tokenSpent = agent.token_spent_usd ?? 0;
    const tokenRemaining = Math.max(0, tokenBudget - tokenSpent);
    const tradingBudget = agent.trading_budget_usd ?? agent.starting_trading_budget ?? 0;

    const allSyms = ['BTC', 'ETH', 'XRP'];

    return (
      <div style={arenaStyles.agentCard(isLeader)}>
        <div style={arenaStyles.agentHeader(color)}>
          <h3 style={{ ...arenaStyles.agentName, color }}>
            <span style={arenaStyles.aliveDot(alive)} />
            {name}
            {isLeader && <span style={arenaStyles.leaderBadge}>👑 LEADING</span>}
          </h3>
          <span style={arenaStyles.modelBadge}>{getModelShort(model)}</span>
        </div>
        <div style={arenaStyles.agentBody}>
          {/* ── Core Stats ── */}
          <div style={arenaStyles.agentStatsGrid}>
            <div style={arenaStyles.agentStat}>
              <span style={arenaStyles.agentStatLabel}>PnL</span>
              <span style={arenaStyles.agentStatValue(pnlColor(agentPnl))}>{fmtUsd(agentPnl)}</span>
            </div>
            <div style={arenaStyles.agentStat}>
              <span style={arenaStyles.agentStatLabel}>Win Rate</span>
              <span style={arenaStyles.agentStatValue(Number(agentWR) >= 50 ? C.green : Number(agentWR) > 0 ? C.red : C.textMuted)}>
                {agentWR}%
              </span>
            </div>
            <div style={arenaStyles.agentStat}>
              <span style={arenaStyles.agentStatLabel}>Record</span>
              <span style={arenaStyles.agentStatValue()}>
                <span style={{ color: C.green }}>{agentWins}W</span>
                <span style={{ color: C.textMuted }}> - </span>
                <span style={{ color: C.red }}>{agentLosses}L</span>
              </span>
            </div>
            <div style={arenaStyles.agentStat}>
              <span style={arenaStyles.agentStatLabel}>Trades / Skips</span>
              <span style={arenaStyles.agentStatValue()}>{totalTrades} / {skips}</span>
            </div>
            <div style={arenaStyles.agentStat}>
              <span style={arenaStyles.agentStatLabel}>Selectivity</span>
              <span style={arenaStyles.agentStatValue(Number(quality.selectivity) < 10 ? C.yellow : C.textPrimary)}>{quality.selectivity}%</span>
            </div>
            <div style={arenaStyles.agentStat}>
              <span style={arenaStyles.agentStatLabel}>Parse Fails</span>
              <span style={arenaStyles.agentStatValue(quality.parseFails > 0 ? C.red : C.green)}>{quality.parseFails}</span>
            </div>
          </div>

          {/* ── Mini PnL Chart ── */}
          {chartData.length > 1 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: '0.7rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8, fontWeight: 600 }}>
                PnL Trajectory
              </div>
              <div style={{ width: '100%', height: 120 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: C.textMuted, fontSize: 9, fontFamily: C.fontMono }} tickLine={false} axisLine={{ stroke: C.border }} />
                    <YAxis tick={{ fill: C.textMuted, fontSize: 9, fontFamily: C.fontMono }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} width={45} />
                    <Tooltip content={<BalanceTooltip />} />
                    <Line
                      type="monotone" dataKey="balance" stroke={color} strokeWidth={2}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        if (!payload.outcome) return <circle key={`agdot-${cx}`} cx={cx} cy={cy} r={2.5} fill={color} stroke="none" />;
                        const dotColor = payload.outcome === 'win' ? C.green : C.red;
                        return <circle key={`agdot-${cx}`} cx={cx} cy={cy} r={3.5} fill={dotColor} stroke={C.bgCard} strokeWidth={1.5} />;
                      }}
                      activeDot={{ r: 4, fill: color, stroke: C.bgCard, strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* ── Symbol Breakdown ── */}
          <div style={{ fontSize: '0.7rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 600 }}>
            Performance by Symbol
          </div>
          <div style={arenaStyles.symbolGrid}>
            {allSyms.map(sym => {
              const s = symbolBreakdown[sym] || { trades: 0, wins: 0, losses: 0, pnl: 0 };
              const wr = (s.wins + s.losses) > 0 ? ((s.wins / (s.wins + s.losses)) * 100).toFixed(0) : '—';
              return (
                <div key={sym} style={arenaStyles.symbolCard(sym)}>
                  <div style={arenaStyles.symbolCardName(sym)}>{sym}</div>
                  <div style={arenaStyles.symbolCardStat}>
                    <span>Trades</span>
                    <span style={arenaStyles.symbolCardVal()}>{s.trades}</span>
                  </div>
                  <div style={arenaStyles.symbolCardStat}>
                    <span>Win Rate</span>
                    <span style={arenaStyles.symbolCardVal(wr !== '—' && Number(wr) >= 50 ? C.green : wr !== '—' ? C.red : C.textMuted)}>{wr}{wr !== '—' ? '%' : ''}</span>
                  </div>
                  <div style={arenaStyles.symbolCardStat}>
                    <span>PnL</span>
                    <span style={arenaStyles.symbolCardVal(pnlColor(s.pnl))}>{s.pnl > 0 ? '+' : ''}{s.pnl}¢</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Decision Quality ── */}
          <div style={arenaStyles.qualityGrid}>
            <div style={arenaStyles.qualityItem}>
              <span style={arenaStyles.qualityLabel}>Trade/Skip Ratio</span>
              <span style={arenaStyles.qualityValue()}>{quality.tradeSkipRatio}</span>
            </div>
            <div style={arenaStyles.qualityItem}>
              <span style={arenaStyles.qualityLabel}>Avg Confidence (Trades)</span>
              <span style={arenaStyles.qualityValue(quality.avgConf ? (Number(quality.avgConf) >= 50 ? C.green : C.yellow) : C.textMuted)}>
                {quality.avgConf ? `${quality.avgConf}%` : '—'}
              </span>
            </div>
            <div style={arenaStyles.qualityItem}>
              <span style={arenaStyles.qualityLabel}>Total Evaluations</span>
              <span style={arenaStyles.qualityValue()}>{quality.totalEvals}</span>
            </div>
            <div style={arenaStyles.qualityItem}>
              <span style={arenaStyles.qualityLabel}>Trades Placed</span>
              <span style={arenaStyles.qualityValue()}>{quality.totalPlaced}</span>
            </div>
          </div>

          {/* ── Budgets ── */}
          <div style={arenaStyles.budgetSection}>
            <div style={arenaStyles.budgetRow}>
              <span>Token Budget</span>
              <span style={{ fontFamily: C.fontMono, color: C.textPrimary }}>
                ${tokenRemaining.toFixed(2)} / ${(agent.starting_token_budget ?? tokenBudget).toFixed(2)}
              </span>
            </div>
            <div style={arenaStyles.budgetBarTrack}>
              <div style={arenaStyles.budgetBarFill(tokenPct(agent), C.yellow)} />
            </div>

            <div style={arenaStyles.budgetRow}>
              <span>Trading Budget</span>
              <span style={{ fontFamily: C.fontMono, color: C.textPrimary }}>
                ${tradingBudget.toFixed(2)}
              </span>
            </div>
            <div style={arenaStyles.budgetBarTrack}>
              <div style={arenaStyles.budgetBarFill(tradingPct(agent), color)} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Full Activity Feed for an agent ── */
  function renderActivityFeed(agent, color) {
    const allTrades = agent.trades || agent.recent_trades || [];
    if (allTrades.length === 0) return <p style={S.noData}>No activity yet</p>;

    return (
      <div style={{ ...S.scrollArea, maxHeight: 400 }} className="cug-scroll">
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Time</th>
            <th style={S.th}>Symbol</th>
            <th style={S.th}>Action</th>
            <th style={S.th}>Contracts</th>
            <th style={S.th}>Price</th>
            <th style={S.th}>Outcome</th>
            <th style={S.th}>PnL</th>
            <th style={S.th}>Reasoning</th>
          </tr></thead>
          <tbody>
            {[...allTrades].reverse().map((t, i) => (
              <tr key={i} className="cug-tr-hover">
                <td style={S.td}>{formatTime(t.placed_at || t.timestamp)}</td>
                <td style={S.td}><span style={S.symbol(t.symbol)}>{t.symbol}</span></td>
                <td style={S.td}><span style={S.action(t.action)}>{t.action}</span></td>
                <td style={S.td}>{t.contracts || 0}</td>
                <td style={S.td}>{t.price_cents ? `${t.price_cents}¢` : '—'}</td>
                <td style={S.td}><span style={S.outcome(t.outcome || 'skipped')}>{t.outcome || 'skipped'}</span></td>
                <td style={{ ...S.td, color: pnlColor(t.pnl_cents) }}>{t.pnl_cents ? `${t.pnl_cents}¢` : '—'}</td>
                <td style={S.tdReason}>{t.reasoning}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Arena uptime ──
  let uptimeStr = '—';
  if (arenaStarted) {
    const diff = Date.now() - new Date(arenaStarted).getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(mins / 60);
    uptimeStr = hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins}m`;
  }

  return (
    <div style={arenaStyles.wrapper}>

      {/* ════ STANDINGS SCOREBOARD ════ */}
      <div style={arenaStyles.scoreboard}>
        <div style={arenaStyles.scoreAgent('left')}>
          <div style={arenaStyles.scoreAgentName(alphaColor)}>
            <span style={arenaStyles.aliveDot(alpha.alive !== false)} />
            {alpha.name || 'Alpha'}
            <span style={arenaStyles.modelBadge}>{getModelShort(alpha.model)}</span>
          </div>
          <div style={arenaStyles.scorePnl(pnlColor(alphaPnl))}>{fmtUsd(alphaPnl)}</div>
          <div style={arenaStyles.scoreRecord}>
            <span style={arenaStyles.scoreRecordWin}>{alphaWins}W</span>
            <span>-</span>
            <span style={arenaStyles.scoreRecordLoss}>{alphaLosses}L</span>
            <span style={{ color: C.textMuted }}>({alphaTotalTrades} trades)</span>
          </div>
          {alphaLeading && pnlDiff > 0 && <span style={arenaStyles.scoreLeadBadge}>👑 Leading by {fmtUsd(pnlDiff)}</span>}
        </div>

        <div style={arenaStyles.scoreCenter}>
          <div style={arenaStyles.scoreVs}>VS</div>
          <div style={arenaStyles.scoreMeta}>
            {cycles} cycles<br />
            {uptimeStr} uptime
          </div>
        </div>

        <div style={arenaStyles.scoreAgent('right')}>
          <div style={{ ...arenaStyles.scoreAgentName(betaColor), justifyContent: 'flex-end' }}>
            <span style={arenaStyles.modelBadge}>{getModelShort(beta.model)}</span>
            {beta.name || 'Beta'}
            <span style={arenaStyles.aliveDot(beta.alive !== false)} />
          </div>
          <div style={{ ...arenaStyles.scorePnl(pnlColor(betaPnl)), textAlign: 'right' }}>{fmtUsd(betaPnl)}</div>
          <div style={{ ...arenaStyles.scoreRecord, justifyContent: 'flex-end' }}>
            <span style={{ color: C.textMuted }}>({betaTotalTrades} trades)</span>
            <span style={arenaStyles.scoreRecordWin}>{betaWins}W</span>
            <span>-</span>
            <span style={arenaStyles.scoreRecordLoss}>{betaLosses}L</span>
          </div>
          {!alphaLeading && pnlDiff > 0 && <span style={{ ...arenaStyles.scoreLeadBadge, alignSelf: 'flex-end' }}>👑 Leading by {fmtUsd(pnlDiff)}</span>}
        </div>
      </div>

      {/* ════ HEAD-TO-HEAD COMPARISON ════ */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <h2 style={S.cardHeaderH2}>📊 Head-to-Head Comparison</h2>
          <div style={{ display: 'flex', gap: 16, fontSize: '0.72rem', fontFamily: C.fontMono }}>
            <span style={{ color: alphaColor }}>■ {alpha.name || 'Alpha'}</span>
            <span style={{ color: betaColor }}>■ {beta.name || 'Beta'}</span>
          </div>
        </div>
        <div style={S.cardBody}>
          <H2HBar label="Win Rate" alphaVal={Number(alphaWR)} betaVal={Number(betaWR)} formatFn={v => `${v}%`} />
          <H2HBar label="Trades" alphaVal={alphaTotalTrades} betaVal={betaTotalTrades} />
          <H2HBar label="Wins" alphaVal={alphaWins} betaVal={betaWins} />
          <H2HBar label="PnL (cents)" alphaVal={Math.max(0, alpha.stats?.total_pnl_cents ?? 0)} betaVal={Math.max(0, beta.stats?.total_pnl_cents ?? 0)} formatFn={v => `${v}¢`} />
          <H2HBar label="Skips" alphaVal={alphaSkips} betaVal={betaSkips} />
          <H2HBar label="Token Spend" alphaVal={parseFloat((alpha.token_spent_usd ?? 0).toFixed(3))} betaVal={parseFloat((beta.token_spent_usd ?? 0).toFixed(3))} formatFn={v => `$${v}`} />
        </div>
      </div>

      {/* ════ AGENT DETAIL CARDS ════ */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <h2 style={S.cardHeaderH2}>⚔️ Agent Performance</h2>
          <span style={S.lastUpdated}>{cycles} cycles completed</span>
        </div>
        <div style={S.cardBody}>
          <div style={arenaStyles.metaRow}>
            <span style={arenaStyles.metaItem}>🕐 Started: {formatDateTime(arenaStarted)}</span>
            <span style={arenaStyles.metaItem}>🔄 Cycles: {cycles}</span>
            <span style={arenaStyles.metaItem}>⏱ Uptime: {uptimeStr}</span>
          </div>
          <div style={arenaStyles.agentGrid}>
            {renderAgentCard(alpha, alphaPnl, alphaWR, alphaWins, alphaLosses, alphaColor, alphaLeading, alphaPnlChart, alphaQuality, alphaSymbols)}
            {renderAgentCard(beta, betaPnl, betaWR, betaWins, betaLosses, betaColor, !alphaLeading, betaPnlChart, betaQuality, betaSymbols)}
          </div>
        </div>
      </div>

      {/* ════ TRADE REASONING BREAKDOWN ════ */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <h2 style={S.cardHeaderH2}>🧠 Trade Reasoning Breakdown</h2>
          <span style={S.lastUpdated}>
            {((alpha.trades || []).filter(t => t.action !== 'skip').length) + ((beta.trades || []).filter(t => t.action !== 'skip').length)} placed trades
          </span>
        </div>
        <div style={S.cardBody}>
          {[
            { agent: alpha, color: alphaColor, label: alpha.name || 'Alpha' },
            { agent: beta, color: betaColor, label: beta.name || 'Beta' },
          ].map(({ agent, color, label }) => {
            const placed = (agent.trades || []).filter(t => t.action !== 'skip');
            if (placed.length === 0) return (
              <div key={label} style={{ marginBottom: 20 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                  background: `linear-gradient(135deg, ${C.bgSecondary}, ${color}12)`,
                  borderRadius: '10px 10px 0 0', borderBottom: `2px solid ${color}30`,
                }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}` }} />
                  <span style={{ fontSize: '0.9rem', fontWeight: 700, color, letterSpacing: '-0.3px' }}>{label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.72rem', fontFamily: C.fontMono, color: C.textMuted }}>0 trades</span>
                </div>
                <p style={{ ...S.noData, background: C.bgSecondary, borderRadius: '0 0 10px 10px', margin: 0, padding: '20px 0' }}>No trades placed yet</p>
              </div>
            );

            return (
              <div key={label} style={{ marginBottom: 24 }}>
                {/* Agent label header */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                  background: `linear-gradient(135deg, ${C.bgSecondary}, ${color}12)`,
                  borderRadius: '10px 10px 0 0', borderBottom: `2px solid ${color}30`,
                }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}` }} />
                  <span style={{ fontSize: '0.9rem', fontWeight: 700, color, letterSpacing: '-0.3px' }}>{label}</span>
                  <span style={{
                    marginLeft: 8, fontSize: '0.68rem', fontWeight: 600, fontFamily: C.fontMono,
                    padding: '2px 8px', borderRadius: 6, background: `${color}18`, color,
                  }}>
                    {getModelShort(agent.model)}
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.72rem', fontFamily: C.fontMono, color: C.textMuted }}>
                    {placed.length} trade{placed.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {/* Trade reasoning table */}
                <div style={{ ...S.scrollArea, maxHeight: 380, borderRadius: '0 0 10px 10px', border: `1px solid ${C.border}`, borderTop: 'none' }} className="cug-scroll">
                  <table style={S.table}>
                    <thead><tr>
                      <th style={S.th}>Time</th>
                      <th style={S.th}>Symbol</th>
                      <th style={S.th}>Action</th>
                      <th style={S.th}>Contracts</th>
                      <th style={S.th}>Price</th>
                      <th style={S.th}>Outcome</th>
                      <th style={S.th}>PnL</th>
                      <th style={{ ...S.th, minWidth: 260 }}>Reasoning</th>
                    </tr></thead>
                    <tbody>
                      {[...placed].reverse().map((t, i) => (
                        <tr key={i} className="cug-tr-hover">
                          <td style={S.td}>{formatTime(t.placed_at || t.timestamp)}</td>
                          <td style={S.td}><span style={S.symbol(t.symbol)}>{t.symbol}</span></td>
                          <td style={S.td}><span style={S.action(t.action)}>{t.action}</span></td>
                          <td style={S.td}>{t.contracts || 0}</td>
                          <td style={S.td}>{t.price_cents ? `${t.price_cents}¢` : '—'}</td>
                          <td style={S.td}><span style={S.outcome(t.outcome || 'pending')}>{t.outcome || 'pending'}</span></td>
                          <td style={{ ...S.td, color: pnlColor(t.pnl_cents), fontWeight: 600 }}>
                            {t.pnl_cents != null ? `${t.pnl_cents > 0 ? '+' : ''}${t.pnl_cents}¢` : '—'}
                          </td>
                          <td style={{
                            ...S.tdReason,
                            maxWidth: 380,
                            fontSize: '0.76rem',
                            lineHeight: 1.5,
                            color: C.textSecondary,
                          }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '6px 10px',
                              background: `${color}08`,
                              borderLeft: `3px solid ${color}40`,
                              borderRadius: '0 6px 6px 0',
                              fontStyle: 'italic',
                            }}>
                              "{t.reasoning || 'No reasoning provided'}"
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ════ FULL ACTIVITY FEED ════ */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <h2 style={S.cardHeaderH2}>📋 Full Activity Feed</h2>
          <span style={S.lastUpdated}>
            {(alpha.trades || []).length + (beta.trades || []).length} total decisions
          </span>
        </div>
        <div style={arenaStyles.tabRow}>
          <button style={arenaStyles.tab(activeTab === 'alpha')} onClick={() => setActiveTab('alpha')}>
            {alpha.name || 'Alpha'} ({(alpha.trades || []).length})
          </button>
          <button style={arenaStyles.tab(activeTab === 'beta')} onClick={() => setActiveTab('beta')}>
            {beta.name || 'Beta'} ({(beta.trades || []).length})
          </button>
        </div>
        <div style={S.cardBody}>
          {activeTab === 'alpha' ? renderActivityFeed(alpha, alphaColor) : renderActivityFeed(beta, betaColor)}
        </div>
      </div>

    </div>
  );
}

/* ── Sub-components ── */

function StatCard({ label, value, kind }) {
  return (
    <div style={S.statCard} className="cug-stat-hover">
      <div style={S.statLabel}>{label}</div>
      <div style={S.statValue(kind)}>{value}</div>
    </div>
  );
}

function SI({ label, value }) {
  return <div style={S.sessionItem}><span style={S.sessionLabel}>{label}</span><span style={S.sessionValue}>{value}</span></div>;
}

function WinStat({ label, value, color }) {
  return <div style={S.winStat}><span style={S.winLabel}>{label}</span><span style={S.winValue(color)}>{value}</span></div>;
}

function ConfMeter({ v }) {
  if (v == null) return <span>—</span>;
  const pct = (v * 100).toFixed(0);
  return (
    <span style={S.confWrap}>
      <span>{pct}%</span>
      <span style={S.confBar}><span style={S.confFill(v)} /></span>
    </span>
  );
}

export default CugPage;